import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/CustomNavbar/Navbar";
import NavbarDesktop from "../components/CustomNavbar/NavbarDesktop";
import {
  R_ABCDRoutes,
  R_GeneralElementsRoutes,
  R_HFL_L3_Routes,
  R_HI_L3_Routes,
  R_HiRoutes,
  R_HLRoutes,
  R_HomeRoutes,
  R_LI_L3_Routes,
  R_LiRoutes,
  R_MF_L3_Routes,
  R_MfRoutes,
  R_SSRoutes,
} from "../utils/Routes";
import { getLobNameByRouteOrUserParam } from "../utils/helper";
import { middleViewPoint } from "../utils/constants";
import useWindowSize from "./useWindowSize";
import { useAppTranslation } from "./useAppTranslation";

const NAVBAR_VISIBLE_ROUTES = [
  R_GeneralElementsRoutes.search,
  R_HLRoutes.home_loan_details,
  R_HLRoutes.interest_certificate,
  R_HLRoutes.emi_payment,
  R_HLRoutes.account_statement,
  R_HLRoutes.change_address,
  R_HLRoutes.document_upload,
  R_HLRoutes.home_loan_service_request,
  R_LiRoutes.details_PolicyNumber,
  R_LiRoutes.details_PolicyNumber_TaxCertificate,
  R_LiRoutes.detail_PolicyNumber_AddUpdateBankAccount,
  R_LiRoutes.details_PolicyNumber_PolicyAccountStatement,
  R_LiRoutes.details_PolicyNumber_PremiumReceipts,
  R_LiRoutes.details_PolicyNumber_UpdatePAN,
  R_LiRoutes.details_set_alerts,
  R_LiRoutes.detail_PolicyNumber_AddUpdateBankAccount,
  R_LiRoutes.li_PanAadharLinking,
  R_MfRoutes.mutualFund_Details,
  R_MfRoutes.mutualFund_CapitalGains,
  R_MfRoutes.mutualFund_AccountStatement,
  R_MfRoutes.mutualFund_ExitLoad,
  R_SSRoutes.details_Portfolio,
  R_SSRoutes.details_Portfolio_MyDocuments,
  R_SSRoutes.contact_notes,
  R_SSRoutes.account_statement,
  R_HiRoutes.details_PolicyNumber,
  R_HiRoutes.details_PolicyNumber_MedicalReports,
  R_HiRoutes.details_PolicyNumber_EditPolicy,
  R_LiRoutes.addUpdate_nominee,
  R_HLRoutes.home_loan_service_request,
  R_HLRoutes.home_loan_change_emi_tenure,
  R_HLRoutes.home_loan_change_emi_tenure_charges,
  R_HLRoutes.home_loan_change_emi_tenure_option3,
  R_HLRoutes.home_loan_change_emi_date,
  R_GeneralElementsRoutes.appTracker,
  R_HLRoutes.home_loan_change_email,
  R_HLRoutes.home_loan_change_mobile,
  R_HLRoutes.balance_confirmation_letter,
  R_HLRoutes.change_emi_select_date,
  R_HLRoutes.change_emi_upload_doc,
  R_GeneralElementsRoutes.taxCenter,
  R_ABCDRoutes.viewAll_transaction,
  R_HLRoutes.advance_emi_payment,
  R_HLRoutes.home_loan_roi_history,
  R_HLRoutes.statement_and_document,
  R_HLRoutes.post_disbursement_document,
  R_HLRoutes.re_kyc,
  "",
];
const NAVBAR_VISIBLE_ROUTES_SET = new Set(NAVBAR_VISIBLE_ROUTES);

const HOME_ROUTES_SET = new Set(["/home", "/login"]);

const L2_REVAMP_ROUTES_SET = new Set([
  R_HiRoutes.hi_baseUrl,
  R_LiRoutes.li_baseUrl,
  R_HLRoutes.home_loan_baseUrl,
  R_MfRoutes.mutualFund_Details,
  R_SSRoutes.details_Portfolio,
  "",
]);

const useCsAppNavbar = () => {
  const { pathname } = useLocation();
  const { width } = useWindowSize();
  const { t } = useAppTranslation(["common", "homeLoan"]);

  const isNavbarShow = useMemo(
    () => NAVBAR_VISIBLE_ROUTES_SET.has(pathname),
    [pathname],
  );

  const isHomeRoute = useMemo(() => HOME_ROUTES_SET.has(pathname), [pathname]);

  const isL2RevampRoutes = useMemo(
    () => L2_REVAMP_ROUTES_SET.has(pathname),
    [pathname],
  );

  const isHLL3RevampRoutes = useMemo(
    () => R_HFL_L3_Routes(pathname),
    [pathname],
  );

  const isMFL3RevampRoutes = useMemo(
    () => R_MF_L3_Routes(pathname),
    [pathname],
  );

  const isHIL3RevampRoutes = useMemo(
    () => R_HI_L3_Routes(pathname),
    [pathname],
  );

  const isLIL3RevampRoutes = useMemo(
    () => R_LI_L3_Routes(pathname),
    [pathname],
  );

  const navbar = useMemo(() => {
    if (!pathname) {
      return null;
    }

    const title = getLobNameByRouteOrUserParam(pathname) || "Dashboard";
    const isJsonKey = title.includes(".");
    const finalTitle = isJsonKey ? t(title) : title;

    if (isNavbarShow && !isL2RevampRoutes) {
      if (
        isHLL3RevampRoutes ||
        isMFL3RevampRoutes ||
        isHIL3RevampRoutes ||
        isLIL3RevampRoutes
      ) {
        return null;
      }
      return <Navbar title={finalTitle} />;
    }

    if (isL2RevampRoutes) {
      if (width < middleViewPoint) {
        return <Navbar title={finalTitle} />;
      }
      return <NavbarDesktop />;
    }

    return null;
  }, [
    isHLL3RevampRoutes,
    isL2RevampRoutes,
    isMFL3RevampRoutes,
    isHIL3RevampRoutes,
    isLIL3RevampRoutes,
    isNavbarShow,
    pathname,
    t,
    width,
  ]);

  return {
    pathname,
    navbar,
    isHomeRoute,
    isL2RevampRoutes,
    isHLL3RevampRoutes,
    isMFL3RevampRoutes,
    isHIL3RevampRoutes,
    isLIL3RevampRoutes,
  };
};

export default useCsAppNavbar;
